<template>
  <button
    :type="type"
    class="ui-button"
    :class="[
      `ui-button--${variant}`,
      `ui-button--${size}`,
      {
        'ui-button--block': block,
        'ui-button--icon-only': iconOnly,
      },
    ]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <hs-icon
      v-if="loading"
      name="loader"
      :size="iconSize"
      spin
    />
    <hs-icon
      v-else-if="icon"
      :name="icon"
      :size="iconSize"
    />
    <span v-if="$slots.default" class="ui-button__label">
      <slot />
    </span>
  </button>
</template>

<script>
import HsIcon from "./HsIcon.vue";

export default {
  name: "HsButton",
  components: { HsIcon },
  props: {
    type: {
      type: String,
      default: "button",
    },
    variant: {
      type: String,
      default: "primary",
      validator: (value) =>
        ["primary", "secondary", "ghost", "danger", "success", "dark"].includes(value),
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value),
    },
    icon: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  computed: {
    iconSize() {
      return this.size === "sm" ? 15 : this.size === "lg" ? 19 : 17;
    },
  },
};
</script>

<style scoped>
.ui-button {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 15px;
  display: inline-flex;
  font-weight: 950;
  gap: 0.5rem;
  justify-content: center;
  letter-spacing: 0;
  min-width: max-content;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;
}

.ui-button--sm {
  font-size: 0.78rem;
  min-height: 2.15rem;
  padding: 0 0.72rem;
}

.ui-button--md {
  font-size: 0.86rem;
  min-height: 2.65rem;
  padding: 0 0.95rem;
}

.ui-button--lg {
  border-radius: 17px;
  font-size: 0.94rem;
  min-height: 3.1rem;
  padding: 0 1.2rem;
}

.ui-button--primary {
  background: linear-gradient(135deg, var(--hs-primary, #3293ec), var(--hs-primary-strong, #1d4ed8));
  box-shadow: 0 14px 30px color-mix(in srgb, var(--hs-primary, #3293ec) 26%, transparent);
  color: #fff;
}

.ui-button--secondary {
  background: color-mix(in srgb, var(--hs-surface, #fff) 88%, transparent);
  border-color: var(--hs-border, rgba(148, 163, 184, 0.22));
  color: var(--hs-text, #0f172a);
}

.ui-button--ghost {
  background: transparent;
  color: var(--hs-muted, #64748b);
}

.ui-button--danger {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  box-shadow: 0 14px 30px rgba(239, 68, 68, 0.2);
  color: #fff;
}

.ui-button--success {
  background: linear-gradient(135deg, #22c55e, #15803d);
  box-shadow: 0 14px 30px rgba(34, 197, 94, 0.2);
  color: #fff;
}

.ui-button--dark {
  background: linear-gradient(135deg, #111827, #020617);
  color: #fff;
}

.ui-button:not(:disabled):hover {
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.12);
  transform: translateY(-1px);
}

.ui-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.ui-button--block {
  width: 100%;
}

.ui-button--icon-only {
  padding: 0;
}

.ui-button--icon-only.ui-button--sm {
  width: 2.15rem;
}

.ui-button--icon-only.ui-button--md {
  width: 2.65rem;
}

.ui-button--icon-only.ui-button--lg {
  width: 3.1rem;
}
</style>
